import {
  useIntegrationMqttCreateInputMapping,
  useIntegrationMqttUpdateInputMapping,
  getIntegrationMqttGetAllInputMappingsQueryKey,
  type MqttBroker,
  type MqttInputMapping,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { MqttInputForm, type InputFormData } from './MqttInputForm';

import { Button, SlideOutPanel } from '@/components/ui';

export interface MqttInputPanelProps {
  isOpen: boolean;
  onClose: () => void;
  brokerId?: string;
  inputMapping?: MqttInputMapping | null;
  duplicateFrom?: MqttInputMapping | null;
  brokers?: MqttBroker[];
  onSuccess?: () => void;
}

export function MqttInputPanel({
  isOpen,
  onClose,
  brokerId,
  inputMapping,
  duplicateFrom,
  brokers,
  onSuccess,
}: MqttInputPanelProps) {
  const queryClient = useQueryClient();
  const createMutation = useIntegrationMqttCreateInputMapping();
  const updateMutation = useIntegrationMqttUpdateInputMapping();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!inputMapping;
  const isDuplicating = !!duplicateFrom;

  const title = isEditing
    ? 'Edit Input Mapping'
    : isDuplicating
      ? 'Duplicate Input Mapping'
      : 'Add Input Mapping';

  const subtitle = isEditing
    ? 'Update MQTT input mapping rules'
    : isDuplicating
      ? 'Create a new mapping based on an existing one'
      : 'Translate MQTT messages into input events';

  const getInitialValues = useCallback((): Partial<InputFormData> => {
    if (inputMapping) {
      return {
        brokerId: inputMapping.brokerId,
        name: inputMapping.name ?? '',
        topic: inputMapping.topic,
        inputId: inputMapping.inputId,
        payloadFilter: inputMapping.payloadFilter ?? '',
        payloadExtract: inputMapping.payloadExtract ?? '',
        payloadTransform: inputMapping.payloadTransform ?? '',
      };
    }
    if (duplicateFrom) {
      return {
        brokerId: duplicateFrom.brokerId,
        name: duplicateFrom.name ? `${duplicateFrom.name} (copy)` : '',
        topic: duplicateFrom.topic,
        inputId: duplicateFrom.inputId,
        payloadFilter: duplicateFrom.payloadFilter ?? '',
        payloadExtract: duplicateFrom.payloadExtract ?? '',
        payloadTransform: duplicateFrom.payloadTransform ?? '',
      };
    }
    return {
      brokerId: brokerId,
    };
  }, [inputMapping, duplicateFrom, brokerId]);

  const handleSubmit = async (data: InputFormData) => {
    setError(null);
    try {
      if (isEditing && inputMapping?.id) {
        await updateMutation.mutateAsync({
          id: inputMapping.id,
          data: {
            brokerId: data.brokerId,
            name: data.name || undefined,
            topic: data.topic,
            inputId: data.inputId,
            payloadFilter: data.payloadFilter || undefined,
            payloadExtract: data.payloadExtract || undefined,
            payloadTransform: data.payloadTransform || undefined,
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            brokerId: data.brokerId,
            name: data.name || undefined,
            topic: data.topic,
            inputId: data.inputId,
            payloadFilter: data.payloadFilter || undefined,
            payloadExtract: data.payloadExtract || undefined,
            payloadTransform: data.payloadTransform || undefined,
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: getIntegrationMqttGetAllInputMappingsQueryKey() });
      onSuccess?.();
      onClose();
    } catch (err) {
      const apiError = err as { message?: string };
      if (apiError.message?.includes('already exists')) {
        setError('An input mapping with this ID already exists.');
      } else {
        setError(isEditing ? 'Failed to update input mapping.' : 'Failed to create input mapping.');
      }
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="input-mapping-form" loading={isLoading}>
        {isEditing ? 'Save Changes' : isDuplicating ? 'Create Copy' : 'Create Mapping'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="lg"
      footer={footer}
    >
      <MqttInputForm
        formId="input-mapping-form"
        initialValues={getInitialValues()}
        preselectedBrokerId={brokerId}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        brokers={brokers}
      />
    </SlideOutPanel>
  );
}
